import { LocaleKey } from '../types/app-state';

export type AccountView = 'bookmarks' | 'learning' | 'orders';

export type AccountSlugs = {
    account: string;
    bookmarks: string;
    orders: string;
    learning: string;
};

const DEFAULT_SLUGS: AccountSlugs = {
    account: 'my-account',
    bookmarks: 'bookmarks',
    orders: 'orders',
    learning: 'e-learning',
};

const SLUG_TRANSLATIONS: Partial<Record<LocaleKey, Partial<AccountSlugs>>> = {
    sv: {
        account: 'mitt-konto',
        bookmarks: 'bokmarken',
        orders: 'bestallningar',
        learning: 'e-utbildningar',
    },
    no: {
        account: 'min-konto',
        bookmarks: 'bokmerker',
        orders: 'bestillinger',
        learning: 'e-laering',
    },
    da: {
        account: 'min-konto',
        bookmarks: 'bogmaerker',
        orders: 'bestillinger',
        learning: 'e-laering',
    },
    fi: {
        account: 'oma-tili',
        bookmarks: 'kirjanmerkit',
        orders: 'tilaukset',
        learning: 'verkkokoulutus',
    },
    de: {
        account: 'mein-konto',
        bookmarks: 'lesezeichen',
        orders: 'bestellungen',
        learning: 'e-learning',
    },
    nl: {
        account: 'mijn-account',
        bookmarks: 'bladwijzers',
        orders: 'bestellingen',
        learning: 'e-learning',
    },
    fr: {
        account: 'mon-compte',
        bookmarks: 'favoris',
        orders: 'commandes',
        learning: 'e-learning',
    },
    it: {
        account: 'il-mio-account',
        bookmarks: 'segnalibri',
        orders: 'ordini',
        learning: 'e-learning',
    },
    es: {
        account: 'mi-cuenta',
        bookmarks: 'favoritos',
        orders: 'pedidos',
        learning: 'e-learning',
    },
    'pt-br': {
        account: 'minha-conta',
        bookmarks: 'favoritos',
        orders: 'pedidos',
        learning: 'e-learning',
    },
    'pt-pt': {
        account: 'a-minha-conta',
        bookmarks: 'favoritos',
        orders: 'encomendas',
        learning: 'e-learning',
    },
    'pl-pl': {
        account: 'moje-konto',
        bookmarks: 'zakladki',
        orders: 'zamowienia',
        learning: 'e-learning',
    },
};

export const getAccountSlugs = (locale?: LocaleKey): AccountSlugs => {
    if (!locale || locale.startsWith('en')) return DEFAULT_SLUGS;
    const overrides = SLUG_TRANSLATIONS[locale] ?? SLUG_TRANSLATIONS[locale.split('-')[0] as LocaleKey];
    if (!overrides) return DEFAULT_SLUGS;
    return { ...DEFAULT_SLUGS, ...overrides };
};

export const accountUrl = (slugs: AccountSlugs, view?: AccountView): string => {
    if (!view) return `/${slugs.account}`;
    return `/${slugs.account}/${slugs[view]}`;
};

export type UserProfileUrls = {
    account: string;
    bookmarks: string;
    orders: string;
    learning: string;
};

export const buildUserProfileUrls = (locale?: LocaleKey): UserProfileUrls => {
    const slugs = getAccountSlugs(locale);
    return {
        account: accountUrl(slugs),
        bookmarks: accountUrl(slugs, 'bookmarks'),
        orders: accountUrl(slugs, 'orders'),
        learning: accountUrl(slugs, 'learning'),
    };
};
